import {
  Box,
  Button,
  Center,
  Link as ChakraLink,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Progress,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { getCallUsData, hyphenatePhoneNumber } from '@constants/phoneNumbers';
import { Routes } from '@constants/routes';
import { useSem } from '@contexts/SemContextProvider';
import wordmark from '@public/assets/logo/wordmarks/fsm-wordmark-black.svg';
import { isValidUSPhoneNumber } from '@utils/validatePhone';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';
import { FaCommentDots, FaPhoneAlt } from 'react-icons/fa';

// Simplified Navbar component
const SimplifiedNavbar = () => {
  const { dynamicPhoneNumber } = useSem();
  const callUsData = getCallUsData(dynamicPhoneNumber);

  return (
    <Flex
      as="nav"
      align="center"
      justify="space-between"
      wrap="wrap"
      paddingY={4}
      paddingX={[4, 8]}
      bg="white"
      borderBottom="10px solid"
      borderColor="blue.500"
    >
      <Flex align="center" mr={5} flexShrink={0}>
        <Link href={Routes.index}>
          <Box display="flex" width={[136, 230]} height={['25px', '40px']} position="relative">
            <Image src={wordmark} alt="Fair Square Medicare Wordmark" fill priority />
          </Box>
        </Link>
      </Flex>

      <Flex align="center">
        <Text fontWeight="bold" fontSize={['md', 'lg']}>
          <a href={callUsData.href} style={{ textDecoration: 'none', color: 'inherit', paddingLeft: '4px' }}>
            {hyphenatePhoneNumber(dynamicPhoneNumber)}
          </a>
        </Text>
      </Flex>
    </Flex>
  );
};

export default function MultiStepFormPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 9;
  const { dynamicPhoneNumber } = useSem();
  const posthog = usePostHog();
  const [isEligible, setIsEligible] = useState<string | undefined>(undefined);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [rawPhoneNumber, setRawPhoneNumber] = useState('');
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const [contactPreference, setContactPreference] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const router = useRouter();

  const proceedToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // On the last step, the main button itself handles navigation
      router.push('/');
    }
  };

  const validateAndProceed = () => {
    if (currentStep === 8) {
      if (!isValidUSPhoneNumber(rawPhoneNumber)) {
        setPhoneError('Please enter a valid 10-digit US phone number.');
        return false;
      }
      setPhoneError(null);
    }
    return true;
  };

  const handleNext = () => {
    if (validateAndProceed()) {
      proceedToNextStep();
    }
  };

  const submitDataAndRedirect = React.useCallback(async () => {
    if (hasSubmitted) return;

    setIsSubmitting(true);
    const formData = {
      isEligible,
      phoneNumber: rawPhoneNumber,
      contactPreference,
      source: 'Letter Form',
    };

    try {
      const response = await fetch('/api/notify-slack-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        console.error('Slack notification failed (auto-submit):', await response.text());
      }
    } catch (error) {
      console.error('Error during automatic Slack notification submission:', error);
    }
    setHasSubmitted(true);
    setIsSubmitting(false);
  }, [isEligible, rawPhoneNumber, contactPreference, hasSubmitted, setIsSubmitting, setHasSubmitted]);

  useEffect(() => {
    if (currentStep === totalSteps && !hasSubmitted && !isSubmitting) {
      submitDataAndRedirect();
    }
  }, [currentStep, totalSteps, hasSubmitted, isSubmitting, submitDataAndRedirect]);

  const handleMainButtonAction = async () => {
    if (currentStep < totalSteps) {
      if (validateAndProceed()) {
        proceedToNextStep();
      }
    } else if (currentStep === totalSteps) {
      setIsSubmitting(true);
      if (!hasSubmitted) {
        await submitDataAndRedirect();
      }
      router.push('/');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setPhoneError(null);
      setCurrentStep(currentStep - 1);
      posthog?.capture('multistep_form_footer_navigation', {
        action_type: 'go_back',
        current_step: currentStep,
        target_step: currentStep - 1,
      });
    }
  };

  const progressPercent = (currentStep / totalSteps) * 100;

  const commonTextProps = { fontSize: 'lg', lineHeight: 'tall' };
  const actionButtonProps = { size: 'lg', colorScheme: 'orange', width: 'auto', px: 10 };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentRawValue = e.target.value.replace(/[^\d]/g, '');
    setRawPhoneNumber(currentRawValue);

    let formatted = '';
    const startsWithOne = currentRawValue.startsWith('1');
    let digitsToFormat = startsWithOne ? currentRawValue.substring(1) : currentRawValue;

    if (startsWithOne) {
      if (digitsToFormat.length === 0) {
        formatted = '1';
      } else if (digitsToFormat.length <= 3) {
        formatted = `1 (${digitsToFormat}`;
      } else if (digitsToFormat.length <= 6) {
        formatted = `1 (${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3)}`;
      } else if (digitsToFormat.length <= 10) {
        formatted = `1 (${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
      } else {
        formatted = `1 (${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
        setRawPhoneNumber(currentRawValue.slice(0, 11));
      }
    } else {
      if (digitsToFormat.length <= 3) {
        formatted = digitsToFormat.length > 0 ? `(${digitsToFormat}` : '';
      } else if (digitsToFormat.length <= 6) {
        formatted = `(${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3)}`;
      } else if (digitsToFormat.length <= 10) {
        formatted = `(${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
      } else {
        formatted = `(${digitsToFormat.slice(0, 3)}) ${digitsToFormat.slice(3, 6)}-${digitsToFormat.slice(6, 10)}`;
        setRawPhoneNumber(currentRawValue.slice(0, 10));
      }
    }
    setFormattedPhoneNumber(formatted);
    if (phoneError) setPhoneError(null);
  };

  const handlePhoneStepProceed = () => {
    if (validateAndProceed()) {
      posthog?.capture('multistep_form_step_proceed', {
        step: currentStep,
        button_text: 'Confirm Number',
      });
      proceedToNextStep();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <VStack spacing={4} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              Medicare: Like Layers on a Cold Day
            </Heading>
            <Text {...commonTextProps}>How Medicare is a lot like putting on layers on a cold day.</Text>
            <Text {...commonTextProps}>
              In the same way that you put on layers before going outside on a cold day, you put on layers of health
              insurance when it comes to Medicare. And just like you wouldn't put your coat on before your undershirt,
              there's an order to these layers of health insurance.
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('multistep_form_step_proceed', {
                    step: 1,
                    button_text: 'See the First Layer',
                  });
                  handleNext();
                }}
              >
                See the First Layer
              </Button>
            </Center>
          </VStack>
        );
      case 2:
        return (
          <VStack spacing={3} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              Your First Layer: Part A (Hospital Insurance)
            </Heading>
            <Text {...commonTextProps}>
              <strong>Covers:</strong> Hospitalization, skilled nursing care, and hospice. Full coverage for the first
              60 days in a hospital after a $1,676 deductible (in 2025) is paid. After the 60th day, you pay $419 per
              day (in 2025). After the 91st day, you pay $838 per day (in 2025). After the 150th day, you pay all
              expenses.
            </Text>
            <Text {...commonTextProps}>
              <strong>Premium:</strong> None, if Social Security tax has been withheld from your paycheck for at least
              ten years. Otherwise there's a premium.
            </Text>
            <Text {...commonTextProps}>
              <strong>Provided by:</strong> The federal government.
            </Text>
            <Text {...commonTextProps}>
              <strong>When To Enroll:</strong> Three months before your 65th birthday up to three months after.
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('multistep_form_step_proceed', {
                    step: 2,
                    button_text: 'Explore Part B',
                  });
                  handleNext();
                }}
              >
                Explore Part B
              </Button>
            </Center>
          </VStack>
        );
      case 3:
        return (
          <VStack spacing={3} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              Your Second Layer: Part B (Medical Insurance)
            </Heading>
            <Text {...commonTextProps}>
              <strong>Covers:</strong> Out-Patient care and doctor's charges in a hospital. Examples of Out-Patient care
              are a check-up, X-Ray, blood test, or a simple elective procedure. After meeting an annual deductible
              ($257 in 2025), Part B covers 80% of expenses, and you pay the remaining 20%.
            </Text>
            <Text {...commonTextProps}>
              <strong>Premium:</strong> If your individual tax return shows income of $106,000 (or $212,000 for a joint
              tax return), or less, your Part B premium is $185 per month in 2025. If your income is higher, your
              premium will be as well.
            </Text>
            <Text {...commonTextProps}>
              <strong>Provided by:</strong> The federal government.
            </Text>
            <Text {...commonTextProps}>
              <strong>When To Enroll:</strong> Three months before your 65th birthday up to three months after.
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('multistep_form_step_proceed', {
                    step: 3,
                    button_text: 'Understand the Gaps',
                  });
                  handleNext();
                }}
              >
                Understand the Gaps
              </Button>
            </Center>
          </VStack>
        );
      case 4:
        return (
          <VStack spacing={3} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              A Quick Pause: Gaps in Original Medicare
            </Heading>
            <Text {...commonTextProps}>
              Parts A and B together are referred to as "Original Medicare", and while they satisfy the minimum medical
              coverage the government requires you to have, many folks find they have gaps in the following areas:
            </Text>
            <UnorderedList
              spacing={2}
              pl={5}
              mt={2}
              fontSize={commonTextProps.fontSize}
              lineHeight={commonTextProps.lineHeight}
            >
              <ListItem>
                <strong>Part A Deductible:</strong> Can reset and be paid multiple times.
              </ListItem>
              <ListItem>
                <strong>Part B Coinsurance:</strong> You pay 20% of costs, which can be substantial.
              </ListItem>
              <ListItem>
                <strong>Excess Charges:</strong> Providers may charge more than Medicare reimburses.
              </ListItem>
              <ListItem>
                <strong>Foreign Travel:</strong> Generally not covered.
              </ListItem>
            </UnorderedList>
            <Text {...commonTextProps} mt={2}>
              Further, the government requires you to have Prescription Drug Coverage (Part D). Time to put on some more
              layers.
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('multistep_form_step_proceed', {
                    step: 4,
                    button_text: 'Learn About More Layers',
                  });
                  handleNext();
                }}
              >
                Learn About More Layers
              </Button>
            </Center>
          </VStack>
        );
      case 5:
        return (
          <VStack spacing={3} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              More Layers: Medigap, Medicare Advantage & Part D
            </Heading>
            <Text {...commonTextProps}>
              If you're fine with Original Medicare (Parts A and B), you can purchase an additional Prescription Drug
              Plan (Part D).
            </Text>
            <Text {...commonTextProps}>
              If you want additional coverage to "fill the gaps", you have two main options:
            </Text>
            <Heading as="h3" size="md" mt={3} mb={1}>
              Medigap (Medicare Supplement)
            </Heading>
            <Text {...commonTextProps}>
              Picks up the bill after Original Medicare pays (e.g., Plan G). Offered by private carriers but benefits
              are standardized. You also need a separate Part D plan.
            </Text>
            <Heading as="h3" size="md" mt={3} mb={1}>
              Medicare Advantage Plans
            </Heading>
            <Text {...commonTextProps}>
              Run by private carriers (HMOs or PPOs). You pay copayments/coinsurance up to an out-of-pocket maximum.
              Often include prescription drug coverage. Ensure your doctors are in-network.
            </Text>
            <Text {...commonTextProps} mt={2}>
              <strong>Choosing:</strong> Compare total costs (premium + expected healthcare bills). There's a trial
              period for Medicare Advantage if you enroll at 65.
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('multistep_form_step_proceed', {
                    step: 5,
                    button_text: 'Ready to Get Started?',
                  });
                  handleNext();
                }}
              >
                Ready to Get Started?
              </Button>
            </Center>
          </VStack>
        );
      case 6:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              Are you eligible for Medicare soon?
            </Heading>
            <Text {...commonTextProps} textAlign="center" mb={2}>
              Are you 65 or turning 65 within the next year?
            </Text>
            <HStack justify="center" spacing={4} mt={4} align="center">
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('multistep_form_eligibility_selected', {
                    step: 6,
                    eligibility: 'yes',
                  });
                  setIsEligible('yes');
                  handleNext();
                }}
              >
                Yes
              </Button>
              <Button
                {...actionButtonProps}
                onClick={() => {
                  posthog?.capture('multistep_form_eligibility_selected', {
                    step: 6,
                    eligibility: 'no',
                  });
                  setIsEligible('no');
                  handleNext();
                }}
                colorScheme="gray"
              >
                No
              </Button>
            </HStack>
          </VStack>
        );
      case 7:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              See your plan matches
            </Heading>
            <Text {...commonTextProps} textAlign="center" mb={2}>
              How would you like to get your list of plans?
            </Text>
            <HStack justify="center" spacing={4} mt={4} align="center">
              <Button
                {...actionButtonProps}
                leftIcon={<FaPhoneAlt />}
                onClick={() => {
                  posthog?.capture('multistep_form_contact_preference_selected', {
                    step: 7,
                    preference: 'call',
                  });
                  setContactPreference('call');
                  handleNext();
                }}
              >
                Prefer a Call
              </Button>
              <Button
                {...actionButtonProps}
                leftIcon={<FaCommentDots />}
                onClick={() => {
                  posthog?.capture('multistep_form_contact_preference_selected', {
                    step: 7,
                    preference: 'text',
                  });
                  setContactPreference('text');
                  handleNext();
                }}
              >
                Prefer a Text
              </Button>
            </HStack>
          </VStack>
        );
      case 8:
        return (
          <VStack spacing={6} align="stretch" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              Great news, your plan search is underway...
            </Heading>
            <FormControl isInvalid={!!phoneError}>
              <FormLabel fontSize="lg" textAlign="center" mb={3}>
                Enter your phone number to receive your matches:
              </FormLabel>
              <Center>
                <InputGroup size="lg" maxW="sm">
                  <InputLeftElement pointerEvents="none">
                    <FaPhoneAlt color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="(555) 123-4567"
                    value={formattedPhoneNumber}
                    onChange={handlePhoneNumberChange}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') handlePhoneStepProceed();
                    }}
                    fontSize="lg"
                    pl="3.5rem"
                    width="100%"
                  />
                </InputGroup>
              </Center>
              {phoneError && <FormErrorMessage justifyContent="center">{phoneError}</FormErrorMessage>}
            </FormControl>
            <Text fontSize="2xs" color="gray.800" textAlign="center" mt={3}>
              By submitting my phone number above, I provide my express written consent to receive marketing and
              non-marketing calls and text messages from or on behalf of Virgil Insurance Agency, LLC (DBA Fair Square
              Medicare) at the number I've provided regarding Medicare plans, including Medicare Advantage, Medicare
              Supplement & Prescription Drug Plans, even if my number is on a Do Not Call list and even if an automated
              dialing system or pre-recorded message is used. I understand & agree that my communications with Fair
              Square Medicare are subject to Fair Square Medicare's{' '}
              <ChakraLink href="/privacy-policy" color="blue.500" isExternal>
                Privacy Policy
              </ChakraLink>{' '}
              &{' '}
              <ChakraLink href="/terms-of-service" color="blue.500" isExternal>
                Terms of Service
              </ChakraLink>
              . This is a solicitation of insurance. Contact will be made by a licensed insurance agent. I understand my
              consents to receive text messages and telephone calls are not required for any purchase; I may instead
              reach Fair Square Medicare directly at 888-454-2383; I can revoke my consents at any time by emailing
              donotcall@fairsquaremedicare.com. Message and data rates may apply. Message frequency varies. Text STOP to
              opt-out of messaging.
            </Text>
            <Center mt={6}>
              <Button
                {...actionButtonProps}
                onClick={handlePhoneStepProceed}
                isDisabled={isSubmitting || !isValidUSPhoneNumber(rawPhoneNumber)}
              >
                Confirm Number
              </Button>
            </Center>
          </VStack>
        );
      case 9:
        return (
          <VStack spacing={4} align="center" w="100%">
            <Heading as="h2" size="lg" textAlign="center" mb={4}>
              Thank You!
            </Heading>
            <Text {...commonTextProps} textAlign="center">
              We hope this has helped demystify the world of Medicare for you.
            </Text>
            <Text {...commonTextProps} textAlign="center" fontWeight="bold" mt={2}>
              You will receive a {contactPreference === 'text' ? 'text message' : 'call'} from us soon.
            </Text>
            <Text {...commonTextProps} textAlign="center" mt={4}>
              If you have any immediate questions, please call us at{' '}
              <ChakraLink href={`tel:${dynamicPhoneNumber}`} color="blue.500" isExternal>
                {hyphenatePhoneNumber(dynamicPhoneNumber)}
              </ChakraLink>
              .
            </Text>
          </VStack>
        );
      default:
        return <Text {...commonTextProps}>Step {currentStep}</Text>;
    }
  };

  return (
    <Box>
      <Head>
        <title>Your Medicare Guide - Fair Square Medicare</title>
        <meta name="description" content="A step-by-step guide to understanding Medicare, by Dan Petkevich." />
      </Head>

      <SimplifiedNavbar />

      {/* Main Content Area */}
      <Flex
        direction="column"
        pt={10}
        pb={100}
        px={[4, 6, 8]}
        minH={{
          base: 'calc(100vh - 160px)',
          md: 'calc(100vh - 180px)',
        }}
        alignItems="center"
        justifyContent="flex-start"
      >
        <VStack spacing={8} w="100%" maxW="600px" mt={8}>
          {' '}
          {/* Added margin top for spacing */}
          {renderStepContent()}
        </VStack>
      </Flex>

      {/* Footer with Progress and Navigation */}
      <Box
        as="footer"
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="white"
        borderTop="1px"
        borderColor="gray.200"
        p={4}
        zIndex={10}
      >
        <Flex justify="space-between" align="center" maxW="xl" mx="auto">
          {' '}
          <Button
            onClick={handleBack}
            isDisabled={currentStep === 1 || isSubmitting}
            variant="link"
            fontSize="lg"
            color="gray.600"
          >
            {'< BACK'}
          </Button>
          <VStack spacing={1}>
            <Text fontSize="sm" color="orange.500" fontWeight="bold">
              {currentStep}/{totalSteps}
            </Text>
            <Progress value={progressPercent} size="sm" w="150px" colorScheme="orange" borderRadius="md" />
          </VStack>
          <div style={{ width: '80px' }}></div>
        </Flex>
      </Box>
    </Box>
  );
}
